import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sun, Moon, Sparkles, AlertTriangle, CalendarDays, ArrowRight, Cpu, MapPin, MessageCircle } from "lucide-react";
import { KA_CITIES } from "@/lib/cities";

const API = process.env.REACT_APP_BACKEND_URL;

const Cell = ({ icon: Icon, kn, en, value, sub, accent, testid }) => (
  <div className="flex items-start gap-2.5" data-testid={testid}>
    <span className={`mt-0.5 inline-flex h-8 w-8 flex-none items-center justify-center rounded-full ${accent || "bg-[#A8722A]/12 text-[#A8722A]"}`}>
      <Icon size={16} />
    </span>
    <div className="min-w-0">
      <p className="font-baloo text-xs text-[#B23A2B]">{kn}</p>
      <p className="truncate font-eczar text-base font-semibold text-[#3B221B]">{value}</p>
      <p className="font-mukta text-[11px] text-[#6B5044]">{sub || en}</p>
    </div>
  </div>
);

export const PanchangaWidget = ({ kn }) => {
  const [p, setP] = useState(null);
  const [cityId, setCityId] = useState("bengaluru");
  const today = new Date().toLocaleDateString("en-CA");
  const city = KA_CITIES.find((c) => c.id === cityId) || KA_CITIES[0];

  useEffect(() => {
    setP(null);
    fetch(`${API}/api/panchanga?date=${today}&lat=${city.lat}&lng=${city.lng}`)
      .then((r) => { if (!r.ok) throw new Error("bad"); return r.json(); })
      .then(setP).catch(() => setP(null));
  }, [today, city.lat, city.lng]);

  const niceDate = new Date(today).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const shareText = p
    ? `🕉️ ಇಂದಿನ ಪಂಚಾಂಗ · Today's Panchanga — ${city.en} (${niceDate})\n`
      + `Tithi: ${p.tithi.paksha} ${p.tithi.name}\nNakshatra: ${p.nakshatra.name}\n`
      + `Sunrise ${p.sunrise} · Sunset ${p.sunset}\n`
      + `Abhijit Muhurta: ${p.abhijit_muhurta.start}–${p.abhijit_muhurta.end}\n`
      + `Rahu Kaala (avoid): ${p.inauspicious.rahu_kaala.start}–${p.inauspicious.rahu_kaala.end}\n\n`
      + `Full panchanga & muhurta: https://purohithconnect.com/panchanga`
    : "";

  return (
    <section className="mx-auto max-w-7xl px-4 py-10 md:px-8" data-testid="home-panchanga-widget">
      <div className="overflow-hidden rounded-[22px] border border-[#A8722A]/25 bg-gradient-to-br from-[#FFFDF8] to-[#F6ECDB] shadow-sm">
        <div className="flex flex-col gap-4 border-b border-[#A8722A]/15 p-6 sm:flex-row sm:items-center sm:justify-between md:p-7">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full border border-[#A8722A]/30 bg-[#FFFDF8] px-3 py-1 font-mukta text-[11px] text-[#A8722A]">
              <Cpu size={12} /> {kn ? "ಡೀಪ್-ಟೆಕ್ ಪಂಚಾಂಗ ಯಂತ್ರ" : "Deep-tech Panchanga engine"}
            </p>
            <h2 className="mt-2 font-eczar text-2xl font-bold text-[#3B221B] md:text-3xl">
              {kn ? "ಇಂದಿನ ಪಂಚಾಂಗ" : "Today's Panchanga"}
            </h2>
            <p className="font-mukta text-sm text-[#6B5044]">{niceDate}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-[#A8722A]/30 bg-[#FFFDF8] pl-3 pr-1 py-1">
              <MapPin size={14} className="text-[#A8722A]" />
              <select value={cityId} onChange={(e) => setCityId(e.target.value)} data-testid="widget-city-select"
                className="bg-transparent py-1 pr-1 font-mukta text-sm font-semibold text-[#3B221B] outline-none">
                {KA_CITIES.map((c) => <option key={c.id} value={c.id}>{c.en} · {c.kn}</option>)}
              </select>
            </div>
            {p && (
              <a href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noopener noreferrer" data-testid="widget-whatsapp-share"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#3E6B4F]/40 px-3.5 py-2 font-mukta text-sm font-semibold text-[#3E6B4F] transition-colors hover:bg-[#3E6B4F] hover:text-white">
                <MessageCircle size={15} /> {kn ? "ಹಂಚಿಕೊಳ್ಳಿ" : "Share"}
              </a>
            )}
            <Link to="/panchanga" data-testid="home-panchanga-cta"
              className="inline-flex flex-none items-center justify-center gap-2 rounded-full bg-[#B23A2B] px-5 py-2.5 font-mukta text-sm font-semibold text-[#FFFDF8] transition-colors hover:bg-[#9a2f22]">
              {kn ? "ಪೂರ್ಣ ಪಂಚಾಂಗ" : "Full Panchanga"} <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        {!p ? (
          <p className="p-6 font-mukta text-sm text-[#6B5044]" data-testid="home-panchanga-loading">
            {kn ? "ಪಂಚಾಂಗ ಲೆಕ್ಕ ಹಾಕುತ್ತಿದೆ…" : "Computing today's panchanga…"}
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-5 p-6 sm:grid-cols-3 lg:grid-cols-6 md:p-7">
            <Cell icon={CalendarDays} kn="ತಿಥಿ" en="Tithi" value={p.tithi.name} sub={`${p.tithi.paksha} · upto ${p.tithi.ends_at || "—"}`} testid="widget-tithi" />
            <Cell icon={Sparkles} kn="ನಕ್ಷತ್ರ" en="Nakshatra" value={p.nakshatra.name} sub={`pada ${p.nakshatra.pada}`} testid="widget-nakshatra" />
            <Cell icon={Sun} kn="ಸೂರ್ಯೋದಯ" en="Sunrise" value={p.sunrise} sub={`Sunset ${p.sunset}`} testid="widget-sunrise" />
            <Cell icon={Sparkles} kn="ಅಭಿಜಿತ್ ಮುಹೂರ್ತ" en="Abhijit Muhurta" value={`${p.abhijit_muhurta.start}`} sub={`to ${p.abhijit_muhurta.end}`} accent="bg-[#EAF3EC] text-[#2f6b45]" testid="widget-abhijit" />
            <Cell icon={AlertTriangle} kn="ರಾಹು ಕಾಲ" en="Rahu Kaala" value={p.inauspicious.rahu_kaala.start} sub={`to ${p.inauspicious.rahu_kaala.end}`} accent="bg-[#FBEAE7] text-[#B23A2B]" testid="widget-rahu" />
            <Cell icon={Moon} kn="ಯೋಗ · ಕರಣ" en="Yoga · Karana" value={p.yoga.name} sub={p.karana.name} testid="widget-yoga" />
          </div>
        )}
      </div>
    </section>
  );
};
